import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Popup, useMap } from "react-leaflet";
import type { LatLng } from "leaflet";
import type { MarkerState, PropertySelectionRow } from "../../types";
import { PopupReceptionBook } from "../PopupReceptionBook";
import type { AdditionalFieldConfig } from "@/features/accountSettings/types/additionalFields";
import type { ChibanInput } from "@/types/kaokuNumber";
import type { AddressIntegrator } from "@/utils/AddressIntegrator";
import { type RealEstateRequestModalHandler } from "../../types/realEstateRequest";
import { findReceptionBookRowForMarker } from "../../utils/propertySelection/ensureReceptionBookPropertyRow";
import { CanvasPin } from "../CanvasPin";
import type { CanvasPinMarker } from "../../utils/canvasPin/CanvasPinMarker";
import { getSharedCanvasPinRenderer } from "../../utils/canvasPin/sharedCanvasPinRenderer";
import {
  PIN_COLORS,
  getTeardropPinIcon,
} from "../../utils/canvasPin/teardropPin";

export interface ReceptionBookLayerRef {
  openPopupById: (markerId: number) => void;
}

interface ReceptionBookLayerProps {
  markerStates: MarkerState[];
  crm?: boolean;
  selectedProperties?: PropertySelectionRow[];
  onTagsChange?: (rowId: number, tags: string[]) => void;
  onRemarksChange?: (rowId: number, remarks: string) => void;
  onAdditionalFieldsChange?: (rowId: number, additionalFields: string) => void;
  onReceptionBookPinClick?: (latlng: LatLng) => void;
  handlePinSave?: () => void;
  availableFields?: AdditionalFieldConfig;
  hasAvailableFields?: boolean;
  // 地番検索
  kaokuNumberSearchFlag?: boolean;
  handleKaokuNumberSearch?: (chibanList: ChibanInput[]) => void;
  // 役所資料取得依頼
  realEstateDocumentFetchFlag?: boolean;
  handleRealEstateDocumentFetchModal?: RealEstateRequestModalHandler;
  handleRealEstateResearchRequestModal?: RealEstateRequestModalHandler;
  // 物件概要書
  propertySummary?: boolean;
  handleNewPropertySheet?: (adi: AddressIntegrator) => void;
}

/**
 * 受付帳マーカーレイヤー
 * 受付帳データをReactコンポーネントとしてレンダリングするため、
 * インタラクティブな機能（タグ、備考の編集など）が動作します。
 * ピンは地図で共有する1枚の canvas に描く（最大500件を DOM のマーカーで描くと重いため）
 */
export const ReceptionBookLayer = forwardRef<
  ReceptionBookLayerRef,
  ReceptionBookLayerProps
>(
  (
    {
      markerStates,
      crm = false,
      selectedProperties = [],
      onTagsChange,
      onRemarksChange,
      onAdditionalFieldsChange,
      onReceptionBookPinClick,
      handlePinSave,
      availableFields,
      hasAvailableFields,
      // 地番検索
      kaokuNumberSearchFlag = false,
      handleKaokuNumberSearch,
      // 役所資料取得依頼
      realEstateDocumentFetchFlag = false,
      handleRealEstateDocumentFetchModal,
      handleRealEstateResearchRequestModal,
      // 物件概要書
      propertySummary = false,
      handleNewPropertySheet,
    },
    ref
  ) => {
    const map = useMap();

    // markerStatesが更新された時、最初の一度のみ地図を動かすようにするためのフラグ
    const isMapMovedRef = useRef(false);

    // 各マーカーのrefを_idをキーにして保持
    const markerRefs = useRef<Map<number, CanvasPinMarker>>(new Map());

    // 外部からポップアップを開くためのメソッドを公開
    // _idを使ってマーカーを識別する
    useImperativeHandle(ref, () => ({
      openPopupById: (markerId: number) => {
        const marker = markerRefs.current.get(markerId);
        if (marker) {
          marker.openPopup();
        }
      },
    }));

    // 受付帳マーカーが初めて追加された時にマップビューを調整
    useEffect(() => {
      if (isMapMovedRef.current) return;

      if (markerStates.length > 0) {
        if (markerStates[0].latlng) {
          // このコードは初回の一回のみ動かしたいのでisMapMovedRefフラグで管理する
          map.setView(markerStates[0].latlng, 14);
          isMapMovedRef.current = true;
        }
      } else {
        isMapMovedRef.current = false;
      }
    }, [markerStates, map]);

    // ポップアップの最大幅を地図コンテナ幅の80%に設定
    const popupMaxWidth = map ? Math.floor(map.getSize().x * 0.8) : 320;

    const receptionBookRows = crm
      ? selectedProperties.filter((prop) => prop.pinType === "reception-book")
      : [];

    const renderer = getSharedCanvasPinRenderer(map);
    const icon = getTeardropPinIcon(PIN_COLORS.receptionBook);
    // 開くと物件一覧に入るので、選択中は一覧の青で描く
    const selectedIcon = getTeardropPinIcon(PIN_COLORS.listed, {
      selected: true,
    });

    return (
      <>
        {markerStates.map((markerState, index) => {
          if (!markerState.latlng || !markerState.book) {
            return null;
          }

          // bookId + 所在・地番で行を特定（配列 index 依存だと行削除後に誤紐付けになる）
          const propertyRow = findReceptionBookRowForMarker(
            receptionBookRows,
            markerState
          );
          const rowId = propertyRow?.id;
          const receptionBookIndex =
            propertyRow !== undefined
              ? receptionBookRows.findIndex((row) => row.id === propertyRow.id)
              : undefined;
          const syncedReceptionBookIndex =
            receptionBookIndex !== undefined && receptionBookIndex !== -1
              ? receptionBookIndex
              : undefined;

          const markerId = markerState._id;

          return (
            <CanvasPin
              key={`reception-book-${markerId ?? index}`}
              position={markerState.latlng}
              icon={icon}
              selectedIcon={selectedIcon}
              renderer={renderer}
              eventHandlers={{
                click: (e) => {
                  e.originalEvent.stopPropagation();
                },
                popupopen: () => {
                  // ポップアップを開いた時に、その地点の座標をclickedLatLngに書き込むための処理
                  if (onReceptionBookPinClick && markerState.latlng) {
                    onReceptionBookPinClick(markerState.latlng);
                  }
                },
              }}
              ref={(markerRef) => {
                if (markerId !== undefined) {
                  if (markerRef) {
                    markerRefs.current.set(markerId, markerRef);
                  } else {
                    markerRefs.current.delete(markerId);
                  }
                }
              }}
            >
              <Popup maxWidth={popupMaxWidth}>
                <PopupReceptionBook
                  book={markerState.book}
                  lat={markerState.latlng.lat}
                  lng={markerState.latlng.lng}
                  chibanRawData={markerState.chibanRawData}
                  crm={crm}
                  initialTags={crm ? propertyRow?.tags : undefined}
                  onTagsChange={
                    crm && onTagsChange && rowId !== undefined
                      ? (tags: string[]) => {
                          onTagsChange(rowId, tags);
                        }
                      : undefined
                  }
                  initialRemarks={crm ? propertyRow?.remarks : undefined}
                  onRemarksChange={
                    crm && onRemarksChange && rowId !== undefined
                      ? (remarks: string) => {
                          onRemarksChange(rowId, remarks);
                        }
                      : undefined
                  }
                  initialAdditionalFields={
                    crm ? propertyRow?.additionalFields : undefined
                  }
                  onAdditionalFieldsChange={
                    crm && onAdditionalFieldsChange && rowId !== undefined
                      ? (additionalFields: string) => {
                          onAdditionalFieldsChange(rowId, additionalFields);
                        }
                      : undefined
                  }
                  handlePinSave={handlePinSave}
                  receptionBookIndex={
                    crm ? syncedReceptionBookIndex : undefined
                  }
                  availableFields={availableFields}
                  hasAvailableFields={hasAvailableFields}
                  kaokuNumberSearchFlag={kaokuNumberSearchFlag}
                  handleKaokuNumberSearch={handleKaokuNumberSearch}
                  realEstateDocumentFetchFlag={realEstateDocumentFetchFlag}
                  handleRealEstateDocumentFetchModal={
                    handleRealEstateDocumentFetchModal
                  }
                  handleRealEstateResearchRequestModal={
                    handleRealEstateResearchRequestModal
                  }
                  propertySummary={propertySummary}
                  handleNewPropertySheet={handleNewPropertySheet}
                />
              </Popup>
            </CanvasPin>
          );
        })}
      </>
    );
  }
);

ReceptionBookLayer.displayName = "ReceptionBookLayer";
